import React, { useEffect, useState } from 'react';
import { Icon, Popconfirm, Switch, Tag, Tooltip, message } from 'antd';
import styled from 'styled-components';

import { frequency } from './helpers';
import {
  COVERAGE_ONE, GROUP_OK, GROUP_PENDING, GROUP_OVER, GROUP_EXTRA,
  coverageLabel, coverageSentence, describeRule, hasRule,
} from './enrollment';

// ---------------------------------------------------------------------------
// Um grupo de tarefas (TFE e afins) numa linha só: um toggle liga ou desliga
// o cliente em todas as filhas, e a seta abre a lista para escolher uma a uma.
// Em grupo de "só uma", ligar uma filha desliga as outras.
// ---------------------------------------------------------------------------

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 14px;
  border-bottom: 1px solid #f7f7f7;
  background: #fff;
  cursor: pointer;

  &[aria-expanded='true'] { background: #fafcff; }
  &:hover { background: #f0f7ff; }

  .caret {
    width: 14px;
    color: #8c8c8c;
    font-size: 11px;
  }
  .name {
    flex: 1;
    min-width: 0;
    font-size: 13px;
    font-weight: 600;
    color: #262626;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .count {
    font-size: 12px;
    color: #8c8c8c;
    white-space: nowrap;
  }
`;

const Children = styled.div`
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
`;

const Child = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 7px 14px 7px 48px;
  border-top: 1px solid #f5f5f5;
  cursor: pointer;
  font-size: 13px;
  color: #262626;

  &[aria-checked='true'] { background: #f6fbff; font-weight: 500; }
  &:hover { background: #f0f7ff; }

  .name { flex: 1; min-width: 0; }
  .when { font-size: 12px; color: #8c8c8c; white-space: nowrap; }
`;

const Hint = styled.div`
  padding: 6px 14px 6px 48px;
  font-size: 12px;
  color: #8c8c8c;
`;

const STATE = {};
STATE[GROUP_OK] = { label: 'Em dia', color: 'green' };
STATE[GROUP_PENDING] = { label: 'Pendente', color: 'orange' };
STATE[GROUP_OVER] = { label: 'Mais de uma', color: 'red' };
STATE[GROUP_EXTRA] = { label: 'A regra não sugere', color: 'red' };

const GroupRow = ({ group, items, status, onToggle, busyIds, defaultOpen }) => {
  const [open, setOpen] = useState(!!defaultOpen);
  // A busca que acha uma filha abre o grupo para mostrá-la.
  useEffect(() => { if (defaultOpen) setOpen(true); }, [defaultOpen]);
  const busy = busyIds || [];
  const locked = busy.length > 0;
  const single = group.coverage === COVERAGE_ONE;
  const on = status.on > 0;
  const assigned = items.filter((task) => task.is_assigned);
  const loading = items.some((task) => busy.indexOf(task.id) > -1);
  const state = STATE[status.state];

  const toggleGroup = () => {
    if (locked) return;
    if (on) {
      onToggle(assigned, false);
      return;
    }
    if (single) {
      setOpen(true);
      message.info('"' + group.name + '" pede só uma: escolha qual na lista.');
      return;
    }
    onToggle(items.filter((task) => !task.disabled_at), true);
  };

  const toggleChild = async (task) => {
    if (locked) return;
    if (task.is_assigned) {
      onToggle([task], false);
      return;
    }
    // Só uma: liga a escolhida e desliga as outras, para o grupo não ficar
    // com duas.
    await onToggle([task], true);
    const others = assigned.filter((other) => other.id !== task.id);
    if (single && others.length > 0) {
      await onToggle(others, false);
    }
  };

  return (
    <div data-group={group.name}>
      <Head aria-expanded={open} onClick={() => setOpen(!open)}>
        <Icon className="caret" type={open ? 'caret-down' : 'caret-right'} />
        {/* Desligar várias de uma vez pede confirmação; o resto é direto. */}
        <Popconfirm
          title={'Desligar as ' + assigned.length + ' tarefas de "' + group.name + '" deste cliente?'}
          okText="Desligar" okType="danger" cancelText="Cancelar"
          onConfirm={toggleGroup}
          disabled={!(on && assigned.length > 1)}
        >
          <span onClick={(event) => event.stopPropagation()}>
            <Switch size="small" checked={on} loading={loading && !open} data-group-switch={group.name}
              onChange={() => { if (!(on && assigned.length > 1)) toggleGroup(); }} />
          </span>
        </Popconfirm>
        <Icon type="folder" style={{ color: '#faad14' }} />
        <span className="name">{group.name}</span>
        <Tooltip title={hasRule(group) ? 'Sugerido para: ' + describeRule(group) : 'Grupo sem regra'}>
          <Tag style={{ marginRight: 0 }}>{coverageLabel(group.coverage)}</Tag>
        </Tooltip>
        {state && <Tag color={state.color} style={{ marginRight: 0 }} data-group-state={status.state}>{state.label}</Tag>}
        <span className="count" data-group-count>{status.on}/{status.total}</span>
      </Head>
      {open && (
        <Children>
          <Hint>
            {single ? 'Escolha a filha que este cliente recebe — ligar uma desliga as outras.'
              : 'Ligue as filhas que este cliente recebe.'}
            {group.coverage ? ' O grupo ' + coverageSentence(group) + '.' : ''}
          </Hint>
          {items.map((task) => (
            <Child
              key={task.id}
              role="switch"
              aria-checked={!!task.is_assigned}
              data-group-task={task.name}
              onClick={() => toggleChild(task)}
            >
              <Switch size="small" checked={!!task.is_assigned} loading={busy.indexOf(task.id) > -1} />
              <span className="name">
                {task.name}
                {task.disabled_at && <Tag color="red" style={{ marginLeft: 8 }}>desativada</Tag>}
              </span>
              <span className="when">{task.first_date ? frequency(task.frequency, task) : ''}</span>
            </Child>
          ))}
        </Children>
      )}
    </div>
  );
};

export default GroupRow;
